import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CustomInput,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table
} from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown, faArrowLeft, faArrowUp, faPen, faPlus } from '@fortawesome/free-solid-svg-icons'
import Loader from '../../common/Loader'
import useSWRFetch from '../../../helpers/hooks/useSWRFetch'
import handleErrorMessage from '../../../helpers/errors/handleErrorMessage'
import { Toast } from '../../UI/toast/Toast'
import FieldError from '../../marketplace/provider/FieldError'
import { getAdminCategories, reorderAdminCategories, saveAdminCategory } from '../../../helpers/api/marketplace'
import { CATEGORY_ICON_OPTIONS, categoryIcon } from '../../marketplace/marketplaceConstants'

const EMPTY = { name: '', icon: 'toolbox', is_active: true }

const firstErrors = (errors) =>
  Object.entries(errors || {}).reduce((acc, [field, messages]) => ({ ...acc, [field]: [].concat(messages)[0] }), {})

const CategoryModal = ({ category, onClose, onSaved }) => {
  const [form, setForm] = useState(() => (category?.id ? { id: category.id, name: category.name, icon: category.icon, is_active: category.is_active } : EMPTY))
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  const change = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => ({ ...prev, [field]: null }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (form.name.trim().length < 2) {
      setErrors({ name: 'Escribe un nombre de al menos 2 caracteres.' })
      return
    }
    setSaving(true)
    try {
      const response = await saveAdminCategory({ ...form, name: form.name.trim() })
      onSaved(response, form.id ? 'Categoría actualizada.' : 'Categoría creada.')
    } catch (error) {
      const serverErrors = error?.data?.errors
      if (serverErrors) setErrors(firstErrors(serverErrors))
      else handleErrorMessage(error?.data || error)
      setSaving(false)
    }
  }

  return (
    <Modal isOpen toggle={saving ? undefined : onClose} centered>
      <Form onSubmit={handleSubmit} noValidate>
        <ModalHeader toggle={saving ? undefined : onClose}>{form.id ? 'Editar categoría' : 'Nueva categoría'}</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="category-name">Nombre</Label>
            <Input id="category-name" maxLength={100} value={form.name} invalid={Boolean(errors.name)} onChange={({ target }) => change('name', target.value)} placeholder="Ej.: Cerrajería" />
            <FieldError error={errors.name} />
          </FormGroup>
          <FormGroup>
            <Label>Ícono</Label>
            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
              {Object.entries(CATEGORY_ICON_OPTIONS).map(([key, icon]) => (
                <button
                  key={key}
                  type="button"
                  title={key}
                  aria-pressed={form.icon === key}
                  onClick={() => change('icon', key)}
                  className={classNames('d-flex align-items-center justify-content-center rounded-circle border-0', form.icon === key ? 'bg-primary text-white' : 'bg-soft-primary text-primary')}
                  style={{ width: 44, height: 44, fontSize: 17 }}
                >
                  <FontAwesomeIcon icon={icon} />
                </button>
              ))}
            </div>
            <FieldError error={errors.icon} />
          </FormGroup>
          <CustomInput
            type="switch"
            id="category-active"
            label={form.is_active ? 'Activa: se muestra en el Marketplace' : 'Inactiva: no se muestra en el Marketplace'}
            checked={form.is_active}
            onChange={({ target }) => change('is_active', target.checked)}
          />
          {form.id && !form.is_active && (
            <small className="text-600 d-block mt-2">Los servicios que ya la usan siguen publicados, pero nadie podrá elegirla en servicios nuevos.</small>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="falcon-default" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" color="primary" disabled={saving}>
            {saving && <Spinner size="sm" className="mr-2" />}
            Guardar
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

const RootMarketplaceCategories = () => {
  const request = useSWRFetch('marketplace-admin-categories', getAdminCategories)
  const [editing, setEditing] = useState(null)
  const [busy, setBusy] = useState(false)
  const categories = request.data?.data || []

  const saved = (response, message) => {
    request.mutate(response, false)
    setEditing(null)
    Toast.fire({ type: 'success', title: message })
  }

  const move = async (index, offset) => {
    const ids = categories.map((category) => category.id)
    const target = index + offset
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    setBusy(true)
    try {
      request.mutate(await reorderAdminCategories(ids), false)
    } catch (error) {
      handleErrorMessage(error?.data || error)
    } finally {
      setBusy(false)
    }
  }

  const toggle = async (category) => {
    setBusy(true)
    try {
      const response = await saveAdminCategory({ id: category.id, name: category.name, icon: category.icon, is_active: !category.is_active })
      request.mutate(response, false)
      Toast.fire({ type: 'success', title: category.is_active ? 'Categoría desactivada.' : 'Categoría activada.' })
    } catch (error) {
      handleErrorMessage(error?.data || error)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button tag={Link} to="/root/marketplace" color="link" size="sm" className="px-0 mb-2">
        <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
        Volver a los proveedores
      </Button>
      <Card className="mb-3">
        <CardHeader className="bg-light d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
          <div className="mr-auto" style={{ flex: '1 1 320px', minWidth: 0 }}>
            <h5 className="mb-1">Categorías del Marketplace</h5>
            <p className="fs--1 text-600 mb-0">El orden es el mismo en que las ven los propietarios. Una categoría inactiva desaparece de los filtros y del formulario de servicios.</p>
          </div>
          <Button color="primary" size="sm" onClick={() => setEditing({})}>
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Nueva categoría
          </Button>
        </CardHeader>
        {request.error ? (
          <CardBody>
            <Alert color="danger" className="mb-0">
              No pudimos cargar las categorías.
            </Alert>
          </CardBody>
        ) : request.isLoading ? (
          <Loader padding="py-5" />
        ) : (
          <div className="table-responsive">
            <Table className="fs--1 mb-0">
              <thead className="bg-200 text-900">
                <tr>
                  <th style={{ width: 90 }}>Orden</th>
                  <th>Categoría</th>
                  <th className="text-center">Servicios</th>
                  <th>Estado</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category, index) => (
                  <tr key={category.id} className={classNames({ 'text-500': !category.is_active })}>
                    <td className="align-middle text-nowrap">
                      <Button color="falcon-default" size="sm" className="px-2 mr-1" disabled={busy || index === 0} onClick={() => move(index, -1)} title="Subir">
                        <FontAwesomeIcon icon={faArrowUp} />
                      </Button>
                      <Button color="falcon-default" size="sm" className="px-2" disabled={busy || index === categories.length - 1} onClick={() => move(index, 1)} title="Bajar">
                        <FontAwesomeIcon icon={faArrowDown} />
                      </Button>
                    </td>
                    <td className="align-middle">
                      <div className="d-flex align-items-center">
                        <span className="d-flex align-items-center justify-content-center rounded-circle bg-soft-primary text-primary mr-2 flex-shrink-0" style={{ width: 36, height: 36 }}>
                          <FontAwesomeIcon icon={categoryIcon(category)} />
                        </span>
                        <div>
                          <span className="font-weight-semi-bold text-900 d-block">{category.name}</span>
                          <span className="text-600">{category.slug}</span>
                        </div>
                      </div>
                    </td>
                    <td className="align-middle text-center">{category.services_count ?? 0}</td>
                    <td className="align-middle">
                      <Badge color={category.is_active ? 'soft-success' : 'soft-secondary'} pill>
                        {category.is_active ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </td>
                    <td className="align-middle text-right text-nowrap">
                      <Button color="falcon-default" size="sm" className="mr-2" disabled={busy} onClick={() => toggle(category)}>
                        {category.is_active ? 'Desactivar' : 'Activar'}
                      </Button>
                      <Button color="falcon-default" size="sm" disabled={busy} onClick={() => setEditing(category)} title="Editar">
                        <FontAwesomeIcon icon={faPen} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
      {editing && <CategoryModal category={editing} onClose={() => setEditing(null)} onSaved={saved} />}
    </>
  )
}

export default RootMarketplaceCategories
